import { isAxiosError } from "axios";
import { apiClient } from "@/lib/apiClient";
import i18n from "@/utils/i18n";
import type {
  AssignRoleByTitleResult,
  Role,
  RoleFilters,
  UserTitle,
} from "../types/roles-and-permissions.types";

const ADMIN_ENDPOINT = "/api/admin/roles";

/** API response: { success, status, data: RoleApiItem[] | RoleApiItem, message?, count? } */
interface RoleListResponse {
  success?: boolean;
  status?: number;
  data?: RoleApiItem[] | RoleApiItem;
  message?: string;
  count?: number;
  total?: number;
}

/** API'den gelen rol item - backend: id, domainID, name, type, isDefault, createdAt, updatedAt, permissionKeys */
interface RoleApiItem {
  id: string;
  domainID?: string;
  name?: string;
  roleName?: string;
  description?: string;
  roleDescription?: string;
  type?: number;
  isDefault?: boolean;
  assignedPersonCount?: number;
  assignedUserCount?: number;
  usersCount?: number;
  createdAt?: string;
  updatedAt?: string;
  permissionKeys?: string;
  [key: string]: unknown;
}

function mapApiRoleToRole(item: RoleApiItem): Role {
  const name = item.name ?? item.roleName ?? "";
  const description = item.description ?? item.roleDescription ?? "";
  const count =
    item.assignedPersonCount ?? item.assignedUserCount ?? item.usersCount ?? 0;
  const formatDate = (d: string | undefined) => {
    if (!d) return "—";
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) return d;
    return date.toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };
  return {
    id: item.id,
    roleName: name,
    roleDescription: description,
    type: item.type,
    isDefault: item.isDefault,
    permissionKeys:
      typeof item.permissionKeys === "string" ? item.permissionKeys : undefined,
    assignedPersonCount: typeof count === "number" ? count : 0,
    createdAt: formatDate(item.createdAt),
    updatedAt: formatDate(item.updatedAt),
  };
}

export interface CreateRolePayload {
  name: string;
  description?: string;
  permissionKeys: string;
  type?: number;
  isDefault?: boolean;
}

export interface UpdateRolePayload {
  name?: string;
  description?: string;
  permissionKeys?: string;
  type?: number;
  isDefault?: boolean;
}

/**
 * Backend hata gövdesindeki kullanıcıya dönük mesajı çıkarır
 * (örn. 400 errors.PERMISSION_NOT_ALLOWED_FOR_ROLE_TYPE). class-validator
 * dizileri birleştirilir; mesaj yoksa `fallback` döner.
 */
export const extractApiErrorMessage = (
  err: unknown,
  fallback: string,
): string => {
  if (isAxiosError(err)) {
    const data = err.response?.data as
      | { message?: unknown; error?: unknown }
      | undefined;
    const msg = data?.message;
    if (Array.isArray(msg)) {
      const joined = msg.filter((m) => typeof m === "string").join(", ");
      if (joined) return joined;
    }
    if (typeof msg === "string" && msg.trim()) return msg;
    if (typeof data?.error === "string" && data.error.trim()) return data.error;
    return fallback;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
};

export const rolesAndPermissionsApi = {
  getAll: async (params?: {
    page?: number;
    pageSize?: number;
    filters?: RoleFilters;
  }): Promise<{ data: Role[]; total: number }> => {
    const query: Record<string, unknown> = {
      page: params?.page ?? 1,
      limit: params?.pageSize ?? 10,
    };
    if (params?.filters?.search) query.search = params.filters.search;
    if (typeof params?.filters?.roleType === "number") {
      query.roleType = params.filters.roleType;
    }
    if (params?.filters?.sortBy) query.sortBy = params.filters.sortBy;
    if (params?.filters?.sortOrder) query.sortOrder = params.filters.sortOrder;

    const res = await apiClient.get<RoleListResponse>(ADMIN_ENDPOINT, {
      params: query,
    });
    const data = res.data;

    // Backend: { success, status, data: RoleApiItem[], message, count }
    const rawList = data?.data;
    const list: RoleApiItem[] = Array.isArray(rawList)
      ? rawList
      : Array.isArray((data as { items?: RoleApiItem[] })?.items)
        ? (data as { items: RoleApiItem[] }).items
        : [];
    const total =
      data?.count ?? (data as { total?: number })?.total ?? list.length;

    return {
      data: list.map(mapApiRoleToRole),
      total,
    };
  },

  getById: async (
    id: string,
  ): Promise<
    Omit<Role, "permissionKeys"> & { permissionKeys?: string[]; type?: number }
  > => {
    const res = await apiClient.get<RoleListResponse>(
      `${ADMIN_ENDPOINT}/${id}`,
    );
    const data = res.data;
    const item =
      (data?.data as RoleApiItem) ?? (data as unknown as RoleApiItem);
    if (!item?.id) throw new Error(i18n.t("rolesApi.notFound"));

    const role = mapApiRoleToRole(item);
    const type = item.type;
    const keys = item.permissionKeys;
    const permissionKeys: string[] =
      typeof keys === "string"
        ? keys
            .split(/,\s*/)
            .map((p) => p.trim())
            .filter(Boolean)
        : Array.isArray(keys)
          ? (keys as unknown[]).map((k) => String(k))
          : [];

    return { ...role, permissionKeys, type };
  },

  create: async (payload: CreateRolePayload): Promise<Role> => {
    // Hata mesajını çağıran (sayfa) gösterir; interceptor toast'ı mükerrer olmasın.
    const res = await apiClient.post<RoleListResponse>(
      ADMIN_ENDPOINT,
      payload,
      {
        skipErrorToast: true,
      },
    );
    const data = res.data;
    const role =
      (data?.data as RoleApiItem) ?? (data as unknown as RoleApiItem);
    if (!role?.id) throw new Error(i18n.t("rolesApi.createFailed"));
    return mapApiRoleToRole(role);
  },

  update: async (id: string, payload: UpdateRolePayload): Promise<Role> => {
    const res = await apiClient.patch<RoleListResponse>(
      `${ADMIN_ENDPOINT}/${id}`,
      payload,
      { skipErrorToast: true },
    );
    const data = res.data;
    const role =
      (data?.data as RoleApiItem) ?? (data as unknown as RoleApiItem);
    if (!role?.id) throw new Error(i18n.t("rolesApi.updateFailed"));
    return mapApiRoleToRole(role);
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`${ADMIN_ENDPOINT}/${id}`);
  },

  /**
   * GET /api/admin/roles/titles — domaindeki ünvanlar ve personel sayıları.
   *
   * `roleId` verilirse her ünvan için o rolün zaten tanımlı olduğu kişi sayısı
   * da döner; ekran "42 kişiden 5'inde zaten var" bilgisini bundan gösterir.
   */
  getUserTitles: async (params?: {
    search?: string;
    roleId?: string;
    includeInactive?: boolean;
  }): Promise<UserTitle[]> => {
    const res = await apiClient.get<{
      data?: { items?: UserTitle[] } | UserTitle[];
    }>(`${ADMIN_ENDPOINT}/titles`, { params });
    const body = res.data?.data as
      | { items?: UserTitle[] }
      | UserTitle[]
      | undefined;
    const list = Array.isArray(body) ? body : (body?.items ?? []);
    return Array.isArray(list) ? list : [];
  },

  /**
   * POST /api/admin/roles/{id}/assign-by-title — rolü seçilen ünvanlara sahip
   * tüm personele tanımlar.
   *
   * EKLEME semantiği: kişinin mevcut rolleri korunur, rolü zaten olanlar
   * atlanır. Rol kaldırma bu uçtan yapılmaz.
   */
  assignByTitle: async (
    id: string,
    payload: { titles: string[]; includeInactive?: boolean },
  ): Promise<AssignRoleByTitleResult> => {
    const res = await apiClient.post<{ data: AssignRoleByTitleResult }>(
      `${ADMIN_ENDPOINT}/${id}/assign-by-title`,
      payload,
    );
    return res.data.data;
  },
};
